import { useState } from 'react';
import { useParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ContentLayout } from '@/bundle/Layouts/ContentLayout/ContentLayout';
import { DetailsWidget } from '@/components/DetailsWidget/DetailsWidget';
import { Tabs } from '@/components/Tabs/Tabs';
import { Tab } from '@/components/Tabs/ui/Tab/Tab';
import { Box } from '@/components/Box/Box';
import { Typography } from '@/components/Typography/Typography';
import { DATE_TIME_FORMAT, formatDate, FULL_MONTH_DATE_FORMAT } from '@/helpers/dateHelpers/dateHelpers';
import { loadMyVaultCatalog, loadVaultAccount, MY_VAULT_QUERY_KEYS } from '../api/api';
import { getMyVaultPath } from '../path/path';
import { VaultAccountStatus } from '../ui/VaultAccountStatus';
import { ShareSecureLinkModal } from '../ui/ShareSecureLinkModal/ShareSecureLinkModal';
import { ButtonNew } from '@/components-new/ButtonNew/ButtonNew';
import { LuUpload } from 'react-icons/lu';
import { PageBody } from '@/components/Page/PageBody/PageBody';
import { PrimaryDetailsPanel } from '@/bundle/shared/components/PrimaryDetailsPanel/PrimaryDetailsPanel';
import { PrimaryDetailsPanelColumn } from '@/bundle/shared/components/PrimaryDetailsPanel/PrimaryDetailsPanelColumn';
import { PrimaryDetailsPanelHeader } from '@/bundle/shared/components/PrimaryDetailsPanel/styles';
import { Notification } from '@/components/Notification/Notification';
import { DetailsRow } from '@/components/DetailsWidget/DetailsRow/DetailsRow';
import { useStatefulNavigation } from '@/hooks/useStatefulNavigation';
import { Mono } from '@/styles/common';

export const MyVaultAccountDetailsPage = () => {
  const { id } = useParams();
  const { navigateBack } = useStatefulNavigation();
  const queryClient = useQueryClient();
  const [rail, setRail] = useState<string | null>(null);
  const [isShareOpen, setIsShareOpen] = useState(false);

  const { data: catalogResponse } = useQuery({
    queryKey: MY_VAULT_QUERY_KEYS.catalog,
    queryFn: loadMyVaultCatalog,
    staleTime: Infinity,
  });
  const { data } = useQuery({
    queryKey: [MY_VAULT_QUERY_KEYS.account, id],
    queryFn: () => loadVaultAccount(id),
  });
  const account = data?.body;
  const currentRail = rail ?? account?.rails[0];
  const view = account?.rail_views.find((item) => item.rail === currentRail);
  const latest = account?.versions[0];
  const accountName = account
    ? `${account.nickname} · ${account.bank_name} ${account.masked_number}`
    : 'Receiving account';

  return (
    <ContentLayout
      title='My Vault Details'
      backButton='Back to My Vault'
      breadcrumbs='My Vault > Details'
      onBack={() => navigateBack(getMyVaultPath())}
      apiError={data?.error}
      headerAction={
        account && (
          <ButtonNew size='large' onClick={() => setIsShareOpen(true)}>
            <LuUpload style={{ marginRight: '12px' }} size={20} />
            Share
          </ButtonNew>
        )
      }
    >
      <PageBody>
        <Box display='flex' flexDirection='column' rowGap='24px'>
          {account && (
            <PrimaryDetailsPanel
              variant={'#F1EEF8'}
              headerSlot={<PrimaryDetailsPanelHeader>{accountName}</PrimaryDetailsPanelHeader>}
            >
              <PrimaryDetailsPanelColumn name='Status' ratio='1'>
                <VaultAccountStatus status={account.status} />
              </PrimaryDetailsPanelColumn>

              <PrimaryDetailsPanelColumn name='Receiving account ID' ratio='1'>
                {account.ref_id}
              </PrimaryDetailsPanelColumn>

              <PrimaryDetailsPanelColumn name='Created by' ratio='1'>
                {account.created_by.email}
              </PrimaryDetailsPanelColumn>

              <PrimaryDetailsPanelColumn name='Creation date' ratio='1'>
                {formatDate(account.date_created, FULL_MONTH_DATE_FORMAT)}
              </PrimaryDetailsPanelColumn>
            </PrimaryDetailsPanel>
          )}

          {account && (
            <Box display='flex' flexDirection='column' gap='20px' maxWidth={'800px'}>
              <Tabs onChange={setRail}>
                {account.rail_labels.map((item) => (
                  <Tab key={item.code} value={item.code} isActive={currentRail === item.code}>
                    {item.label}
                  </Tab>
                ))}
              </Tabs>

              <DetailsWidget header={`${view?.label ?? ''} info`}>
                {account.is_data_from_blockchain && (
                  <Notification variant='success' fontVariant='medium' verticalAlign='center' marginBottom='20px'>
                    Stored in the WireVault blockchain.
                  </Notification>
                )}
                {!account.is_data_from_blockchain && (
                  <Notification variant='error' fontVariant='medium' verticalAlign='center' marginBottom='20px'>
                    The WireVault blockchain is unavailable. The payment details cannot be confirmed at this time.
                  </Notification>
                )}

                {view && (
                  <>
                    <DetailsRow name='Blockchain ID' title={view.blockchain_id}>
                      {view.blockchain_id}
                    </DetailsRow>
                    {view.fields.map((field) => (
                      <DetailsRow key={field.key} name={field.label}>
                        <span>{field.value || '-'}</span>
                      </DetailsRow>
                    ))}
                  </>
                )}
              </DetailsWidget>

              <Typography variant='bodySmall2' marginTop='8px'>
                Record <Mono>{account.record_hash_short}</Mono> · version {account.record_version} ·{' '}
                {latest?.status === 'success'
                  ? `committed ${formatDate(latest.date_created, DATE_TIME_FORMAT)}, tx ${latest.blockchain_id.slice(0, 10)}`
                  : 'commit pending'}
              </Typography>
            </Box>
          )}
        </Box>
      </PageBody>

      {isShareOpen && catalogResponse?.body && account && (
        <ShareSecureLinkModal
          isOpen
          catalog={catalogResponse.body}
          account={account}
          initialRail={currentRail}
          onClose={() => setIsShareOpen(false)}
          onSuccess={() => queryClient.invalidateQueries({ queryKey: [MY_VAULT_QUERY_KEYS.links] })}
        />
      )}
    </ContentLayout>
  );
};
